import React, { useMemo } from 'react';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, ReferenceLine,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';

/**
 * Reusable RSSI trend chart (dBm over time).
 *
 * Props:
 *  - data: [{ timestamp, rssi, device_imei, device_name }] in chronological order (oldest → newest).
 *  - formatTime: (timestamp) => string  — timezone-aware formatter for the tooltip.
 *  - height: chart height in px (default 96).
 *
 * When the readings come from MORE THAN ONE detecting device the chart superimposes up to 3
 * separate coloured lines (busiest detectors first) so you can compare which tracker is closest to
 * the beacon. A single-device trend keeps the original filled-area look. RSSI is a negative dBm value
 * (closer to 0 = stronger).
 */
const SERIES_COLORS = ['#16a34a', '#2563eb', '#d97706']; // green, blue, amber

const rssiColor = (rssi) => {
  if (rssi == null) return '#64748b';
  if (rssi > -50) return '#16a34a';
  if (rssi > -70) return '#d97706';
  return '#dc2626';
};

const SingleTooltip = ({ active, payload, formatTime }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border bg-popover px-2 py-1 text-[11px] shadow-md" data-testid="rssi-sparkline-tooltip">
      <div className="font-mono font-semibold">{p.rssi} dBm</div>
      <div className="text-muted-foreground">{formatTime ? formatTime(p.timestamp) : p.timestamp}</div>
      {(p.device_name || p.device_imei) && (
        <div className="text-muted-foreground">via {p.device_name || p.device_imei}</div>
      )}
    </div>
  );
};

const MultiTooltip = ({ active, payload, formatTime, nameByKey, colorByKey }) => {
  if (!active || !payload || !payload.length) return null;
  const rows = payload.filter((p) => p.value != null);
  if (!rows.length) return null;
  const ts = rows[0]?.payload?.timestamp;
  return (
    <div className="rounded-md border bg-popover px-2 py-1 text-[11px] shadow-md" data-testid="rssi-sparkline-tooltip">
      <div className="text-muted-foreground mb-0.5">{formatTime ? formatTime(ts) : ts}</div>
      {rows.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: colorByKey[p.dataKey] }} />
          <span className="font-mono font-semibold">{p.value} dBm</span>
          <span className="text-muted-foreground truncate max-w-[120px]">{nameByKey[p.dataKey]}</span>
        </div>
      ))}
    </div>
  );
};

export const RssiSparkline = ({ data = [], formatTime, height = 96 }) => {
  const points = useMemo(() => (data || []).filter((d) => d && d.rssi != null), [data]);

  const { devices, nameByKey, colorByKey, merged } = useMemo(() => {
    const byDevice = {};
    points.forEach((p) => {
      const k = p.device_imei || 'unknown';
      (byDevice[k] = byDevice[k] || []).push(p);
    });
    const devs = Object.keys(byDevice)
      .sort((a, b) => byDevice[b].length - byDevice[a].length)
      .slice(0, 3);
    const names = {};
    const colors = {};
    const seriesMaps = {};
    devs.forEach((k, i) => {
      names[k] = byDevice[k][0]?.device_name || k;
      colors[k] = SERIES_COLORS[i % SERIES_COLORS.length];
      const m = {};
      byDevice[k].forEach((p) => { m[p.timestamp] = p.rssi; });
      seriesMaps[k] = m;
    });
    const tsList = [...new Set(points.map((p) => p.timestamp))].sort();
    const rows = tsList.map((t) => {
      const row = { timestamp: t };
      devs.forEach((k) => { row[k] = seriesMaps[k][t] ?? null; });
      return row;
    });
    return { devices: devs, nameByKey: names, colorByKey: colors, merged: rows };
  }, [points]);

  // Handover crossover points: the moment the strongest (carry-forward) detector changes.
  const crossovers = useMemo(() => {
    if (devices.length < 2) return [];
    const lastVal = {};
    let leader = null;
    const marks = [];
    merged.forEach((row) => {
      devices.forEach((k) => { if (row[k] != null) lastVal[k] = row[k]; });
      let best = null;
      let bestK = null;
      devices.forEach((k) => {
        if (lastVal[k] != null && (best === null || lastVal[k] > best)) { best = lastVal[k]; bestK = k; }
      });
      if (bestK && bestK !== leader) {
        if (leader !== null) marks.push({ timestamp: row.timestamp, from: leader, to: bestK });
        leader = bestK;
      }
    });
    return marks;
  }, [merged, devices]);

  if (points.length < 2) {
    return (
      <div className="flex items-center justify-center text-xs text-muted-foreground" style={{ height }} data-testid="rssi-sparkline-empty">
        Not enough RSSI readings yet to plot a trend.
      </div>
    );
  }

  const values = points.map((p) => p.rssi);
  const domainMin = Math.floor(Math.min(...values) - 5);
  const domainMax = Math.ceil(Math.max(...values) + 5);

  // Multi-device: superimposed coloured lines + a small legend.
  if (devices.length > 1) {
    return (
      <div data-testid="rssi-sparkline" data-multi="true" style={{ width: '100%' }}>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-1" data-testid="rssi-sparkline-legend">
          {devices.map((k) => (
            <span key={k} className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`rssi-sparkline-legend-${k}`}>
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: colorByKey[k] }} />
              <span className="truncate max-w-[160px]">{nameByKey[k]}</span>
            </span>
          ))}
        </div>
        <div style={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={merged} margin={{ top: 12, right: 6, left: 0, bottom: 0 }}>
              <XAxis dataKey="timestamp" hide />
              <YAxis
                domain={[domainMin, domainMax]}
                width={34}
                tick={{ fontSize: 10, fill: 'currentColor' }}
                tickFormatter={(v) => `${v}`}
                axisLine={false}
                tickLine={false}
                className="text-muted-foreground"
              />
              <RechartsTooltip content={<MultiTooltip formatTime={formatTime} nameByKey={nameByKey} colorByKey={colorByKey} />} />
              {crossovers.map((c, ci) => (
                <ReferenceLine
                  key={`xo-${ci}`}
                  x={c.timestamp}
                  stroke="#2563eb"
                  strokeDasharray="3 3"
                  strokeOpacity={0.8}
                  label={{ value: '⇄', position: 'top', fontSize: 12, fill: '#2563eb' }}
                  data-testid={`rssi-sparkline-crossover-${ci}`}
                  ifOverflow="extendDomain"
                />
              ))}
              {devices.map((k) => (
                <Line
                  key={k}
                  type="monotone"
                  dataKey={k}
                  stroke={colorByKey[k]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Single-device: original filled area sparkline.
  const color = rssiColor(points[points.length - 1].rssi);
  const gradId = `rssi-grad-${Math.round(domainMin)}-${Math.round(domainMax)}`;
  return (
    <div data-testid="rssi-sparkline" style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <YAxis
            domain={[domainMin, domainMax]}
            width={34}
            tick={{ fontSize: 10, fill: 'currentColor' }}
            tickFormatter={(v) => `${v}`}
            axisLine={false}
            tickLine={false}
            className="text-muted-foreground"
          />
          <RechartsTooltip content={<SingleTooltip formatTime={formatTime} />} cursor={{ stroke: color, strokeOpacity: 0.4 }} />
          <Area
            type="monotone"
            dataKey="rssi"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradId})`}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RssiSparkline;
